import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Grid } from '@mui/material';
import { GridRenderEditCellParams } from '@mui/x-data-grid';
import { TextField, Switch as FormikSwitch } from 'formik-mui';
import { Field, FormikProps, FormikProvider } from 'formik';
import { formatInTimeZone } from 'date-fns-tz';
import ReactQuill from 'react-quill';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import * as Yup from 'yup';

import { getLanguages, ModuleHandle, useUderlyStore } from '@uderly/react-uderly-ui';
import Module, { ModuleHostProps } from "../components/Module";
import useMediaImage from '../components/Module/hooks/useMediaImage';
import AppContext from '../AppContext';
import IqSpaceApi from '../../api/IqSpace';
import getPlaylistColumns from '../components/getPlaylistColumns';
import Separator from '../components/Separator';
import PlaylistQuestions from '../components/PlaylistQuestions';
import { hasPermission } from '../utils';
import { IqStore } from '../../models/IqStore';
import RemoteSelect from '../../framework/RemoteSelect';
import CoreApi from "../../api/Core";
import useModuleUtils from '../components/Module/hooks/useModuleUtils';
import ActionButton from '../components/ActionButton';
import { StoreBreadcrumbs } from "../components/Module/StoreBreadcrumbs";

interface PackagesProps extends ModuleHostProps {
  storeSubdomain: string;
}

const Packages = (props: PackagesProps) => {
  const { t } = useTranslation();

  const zustandStore = useUderlyStore(s => s.store);
  const user = useUderlyStore(s => s.user);

  const [store, setStore] = useState<IqStore | null>(zustandStore as IqStore);
  const [languageOptions, setLanguageOptions] = useState<[]>();

  useEffect(() => {
    const init = async () => {
      const response = await IqSpaceApi.Store(props.storeSubdomain);

      if (response && response.status === 200) {
        setStore(response.data.data);
      }

      const languagesResponse = await getLanguages();

      if (languagesResponse && languagesResponse.status === 200) {
        setLanguageOptions(CoreApi.mapData(languagesResponse.data.data, "id"));
      }
    }

    init();
  }, [props.storeSubdomain]);

  const moduleData = {
    title: <StoreBreadcrumbs store={store} path="/packages" />,
    name: t('packages'),
    path: `/${store?.subdomain}/packages`,
    apiBasePath: '/packages'
  };

  const moduleRef = useRef<ModuleHandle>(null);

  const [item, setItem] = useState<any>(null);

  const mediaImage = useMediaImage(item, moduleRef, moduleData);
  const moduleUtils = useModuleUtils({ data: moduleData });

  const columns = [
    ...getPlaylistColumns(moduleData.apiBasePath),
    {
      field: 'published',
      headerName: t('published') as string,
      minWidth: 120,
      renderCell: (params: GridRenderEditCellParams) => (
        <div className="info-icons">
          <ActionButton
            size="sm"
            action={async () =>
              await moduleUtils.onTogglePublished(params.row, moduleRef)
            }>
            {params.row.published ? (
              <RadioButtonCheckedIcon />
            ) : (
              <RadioButtonUncheckedIcon />
            )}
          </ActionButton>
        </div>
      ),
    }];

  const initialValues = {
    name: ""
  };

  const schema = () => {
    const v = {
      name: Yup.string().min(2).required((t('required-field') as string))
    };

    return Yup.object(v);
  }

  const onItemLoaded = (item: any) => {
    if (item && item.id) {
      setItem(item);
    }
  };

  const view = (_item: any, formik: FormikProps<any>) => {
    const { values, setFieldValue } = formik;

    if (!store) return null;

    return (
      <FormikProvider value={formik}>
        <Grid container className="package-mask mask">
          <Grid container item spacing={1} sm md lg xl>
            <Grid item xs={12} xl={12}>
              <Field component={TextField} name="name" type="text" readonly
                label={AppContext.r["name"] + "*"} />
            </Grid>

            {hasPermission(user, "content_management") &&
              <Grid item xs={12}>
                <h3>{t('description')}</h3>
                <ReactQuill value={values.description}
                  modules={{
                    clipboard: {
                      matchVisual: false
                    }
                  }}
                  onChange={(value) => setFieldValue('description', value)} />
              </Grid>}

            {values.created_at &&
              <Grid item xs={12} sm={4} xl={4}>
                <div className="outlined-with-label disabled">
                  {formatInTimeZone(values.created_at, values.timezone, "dd/MM/yyyy H:mm:ss")}
                  <label>{AppContext.r["created-at"]}</label>
                </div>
              </Grid>}

            <Grid item xs={6} sm={4} lg={3} marginRight={1}>
              <RemoteSelect name="language_id" key={"language-" + values.language_id} value={values.language_id}
                onChange={(e, value) => {
                  setFieldValue("language_id", value);
                }}
                nullOption={false} fieldToMap="name" options={languageOptions} label={AppContext.r["language"]} />
            </Grid>

            <Grid item sm={2} lg={1}>
              <FormControlLabel control={<Field component={FormikSwitch} type="checkbox" name="published" checked={values.published} />}
                label={AppContext.r["published"]} onChange={(_e, checked) => setFieldValue("published", checked)} />
            </Grid>
          </Grid>

          <Grid item xs={12} sm={3} padding={1} className='media-image-container'>
            {mediaImage.renderComponent(formik, { apiPath: "/playlists" })}
          </Grid>
        </Grid>

        <div className="questions-view">
          {(values && values.questions) &&
            <>
              <h3 className="text-center">{values.questions_count} {AppContext.r["questions"]}</h3>
              <Separator />

              <div className="top-menu"></div>

              <PlaylistQuestions store={store} playlist={values} isPackage packageId={values.id}
                onQuestionsChanged={p => setFieldValue("questions_count", p.length)}
                onQuestionRemoved={_q => setFieldValue("questions_count", values.questions_count - 1)}
                onQuestionInserted={_q => setFieldValue("questions_count", values.questions_count + 1)} />
            </>}
        </div>
      </FormikProvider >
    );
  };

  return (
    <Module
      ref={moduleRef}
      className='playlists'
      columns={columns}
      schema={schema}
      view={view}
      data={moduleData}
      initialValues={initialValues}
      initialFilters={{ store_id: store?.id, language_id: user?.language_id }}
      onItemLoaded={onItemLoaded}
    />
  );
}

export default Packages;